import { Auth } from "aws-amplify";

export interface PasswordFormValues {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export type PasswordFieldErrors = Partial<Record<keyof PasswordFormValues, string>>;

/** Catches the obvious mistakes client-side; Cognito enforces the complexity policy on save. */
export const validatePasswordChange = ({
  currentPassword,
  newPassword,
  confirmPassword,
}: PasswordFormValues): PasswordFieldErrors => {
  const errors: PasswordFieldErrors = {};

  if (!currentPassword) {
    errors.currentPassword = "Please enter your current password";
  }

  if (!newPassword) {
    errors.newPassword = "Please enter a new password";
  } else if (newPassword === currentPassword) {
    errors.newPassword = "New password must be different from your current password";
  }

  if (confirmPassword !== newPassword) {
    errors.confirmPassword = "Passwords do not match";
  }

  return errors;
};

export const hasPasswordErrors = (errors: PasswordFieldErrors): boolean => Object.keys(errors).length > 0;

export interface PasswordChangeError {
  field: keyof PasswordFormValues;
  message: string;
}

/** Maps a Cognito rejection to the field it belongs against so the failure shows inline. */
export const toPasswordChangeError = (error: unknown): PasswordChangeError => {
  const name = error instanceof Error ? error.name : "";
  const serverMessage = error instanceof Error ? error.message : "";

  switch (name) {
    case "NotAuthorizedException":
      return { field: "currentPassword", message: "Your current password is incorrect" };
    case "LimitExceededException":
      return { field: "currentPassword", message: "Too many attempts. Please try again later." };
    case "InvalidPasswordException":
    case "InvalidParameterException":
      return { field: "newPassword", message: serverMessage || "Password does not meet the requirements" };
    default:
      return { field: "newPassword", message: serverMessage || "Failed to change password. Please try again." };
  }
};

/** Changes the signed-in user's Cognito password, verifying the current one. */
export const changePassword = async (currentPassword: string, newPassword: string): Promise<void> => {
  await Auth.changePassword(await Auth.currentAuthenticatedUser(), currentPassword, newPassword);
};
